import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Sidebar } from './components/Sidebar';
import { Toaster } from './components/ui/sonner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { CookieConsent } from './components/CookieConsent';
import DisclaimerGate from './components/DisclaimerGate';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Contact from './pages/Contact';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import KnowledgeBase from './pages/KnowledgeBase';
import ConditionPage from './pages/ConditionPage';
import SymptomTracker from './pages/SymptomTracker';
import SleepTracker from './pages/SleepTracker';
import FoodTracker from './pages/FoodTracker';
import LabTracker from './pages/LabTracker';
import SupplementTracker from './pages/SupplementTracker';
import AppointmentTracker from './pages/AppointmentTracker';
import FlareJournal from './pages/FlareJournal';
import AIChat from './pages/AIChat';
import Community from './pages/Community';
import CopingTools from './pages/CopingTools';
import MedicalTimeline from './pages/MedicalTimeline';
import CycleTracker from './pages/CycleTracker';
import VaccineTracker from './pages/VaccineTracker';
import VoiceJournal from './pages/VoiceJournal';
import PhotoLog from './pages/PhotoLog';
import MyPhotos from './pages/MyPhotos';
import News from './pages/News';
import MedicationReminders from './pages/MedicationReminders';
import Admin from './pages/Admin';
import ReportIssue from './pages/ReportIssue';
import ExportData from './pages/ExportData';
import Landing from './pages/Landing';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import FAQ from './pages/FAQ';
import MedicalDisclaimer from './pages/MedicalDisclaimer';
import PlanSelection from './pages/PlanSelection';
import WeatherTracker from './pages/WeatherTracker';
import PaymentSuccess from './pages/PaymentSuccess';
import './App.css';

// Layout wrapper for authenticated pages
const AppLayout = ({ children }) => {
    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content pt-20 pb-6 px-6 md:pt-8 md:pb-8 md:px-8 lg:pt-10 lg:pb-10 lg:px-10 overflow-y-auto">
                <div className="max-w-7xl mx-auto">
                    {children}
                </div>
            </main>
            <DisclaimerGate />
        </div>
    );
};

// Placeholder components for future features
const PlaceholderPage = ({ title, description, tier }) => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <h1 className="font-heading text-3xl font-bold mb-2">{title}</h1>
        <p className="text-muted-foreground mb-4">{description}</p>
        {tier && (
            <span className="text-sm text-muted-foreground">
                Available in {tier} plan
            </span>
        )}
    </div>
);

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/plans" element={<PlanSelection />} />
                        <Route path="/payment-success" element={<PaymentSuccess />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/medical-disclaimer" element={<MedicalDisclaimer />} />
                        <Route path="/contact" element={<Contact />} />

                        {/* Protected routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Dashboard />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Settings />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Placeholder routes for future features */}
                        <Route
                            path="/knowledge"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <KnowledgeBase />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/knowledge/:conditionId"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ConditionPage />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/symptoms"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <SymptomTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sleep"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <SleepTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/food"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <FoodTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/flares"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <FlareJournal />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/weather"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <WeatherTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/labs"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <LabTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/supplements"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <SupplementTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/appointments"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <AppointmentTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/community"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Community />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/coping"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <CopingTools />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/chat"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <AIChat />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/timeline"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <MedicalTimeline />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/cycle"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <CycleTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/vaccines"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <VaccineTracker />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/voice-journal"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <VoiceJournal />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/photo-log"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <PhotoLog />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/my-photos"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <MyPhotos />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/news"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <News />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/medication-reminders"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <MedicationReminders />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Admin />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/report-issue"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ReportIssue />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/export"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ExportData />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Redirects */}
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    <Toaster position="top-right" richColors />
                    <PWAInstallPrompt />
                    <CookieConsent />
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
